import { FeatureCollection, Geometry } from 'geojson';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { stringify } from 'wkt';
import { useAuth } from '../auth/AuthProvider';
import { Candidate } from '../pages/CandidateList';
import { apiBaseUrl } from '../utils/env';
import { getRequestHeaders } from './helpers';
import { queryKeyUseInsightsPerDateQuery, queryKeyUseInsightsPerDateQueryMinimal } from './query-keys';

const vake_api_server_url = apiBaseUrl();

export const useInsightByUuid = (uuidList: string[] | null) => {
    const { token } = useAuth();
    const uuidListBody = { uuid_list: uuidList };
    const url = `${vake_api_server_url}/insights/uuid`;
    return useQuery<FeatureCollection>(
        ['insights_by_uuid', uuidList],
        async () => {
            const res = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: getRequestHeaders(token),
                body: JSON.stringify(uuidListBody),
            });

            if (res.status === 404) {
                console.log(await res.json());
            }

            if (!res.ok) {
                throw new Error(res.statusText);
            }

            return await res.json();
        },
        {
            staleTime: Infinity,
            enabled: !!uuidList,
            cacheTime: 1000 * 60 * 60,
        }
    );
};

export const useClearviewCandidates = (
    uuidList: string[] | null,
    fromDate: Date | null,
    toDate: Date | null,
    aoi: Geometry | null,
    setClearviewErrorMsg: (value: string | null) => void,
    num_results: number | null = 30
) => {
    const { token } = useAuth();

    const baseUrl = `${vake_api_server_url}/clearview/`;

    // Create query parameters
    const queryParams = new URLSearchParams({});
    if (num_results) {
        queryParams.append('num_results', num_results.toString());
    }

    if (uuidList) {
        uuidList.forEach((uuid) => {
            queryParams.append('uuid_list', uuid);
        });
    }

    if (aoi) {
        queryParams.append('area_of_interest', stringify(aoi));
    }

    if (fromDate) {
        queryParams.append('from_date', fromDate.toISOString());
    }

    if (toDate) {
        queryParams.append('to_date', toDate.toISOString());
    }

    const fullUrl = `${baseUrl}?${queryParams.toString()}`;

    return useQuery<Candidate[]>(
        ['clearviewCandidates', uuidList, fromDate, toDate, aoi],
        async () => {
            const res = await fetch(fullUrl, {
                method: 'GET',
                credentials: 'include',
                headers: getRequestHeaders(token),
            });

            if (!res.ok) {
                if (res.status === 400) {
                    const errorMessage = await res.text();
                    throw new Error(errorMessage);
                } else if (res.status === 401) {
                    throw new Error('Your user does not have access to the VAKE tool for locating dark vessels.');
                } else if (res.status === 408) {
                    throw new Error(
                        'The request was to wide for Clearview to handle. Try decreasing the time period or AOI.'
                    );
                }
                throw new Error(`Error fetching data: ${res.statusText}`);
            } else if (res.status === 204) {
                return [];
            }

            return await res.json();
        },
        {
            staleTime: Infinity,
            enabled: !!uuidList,
            cacheTime: 1000 * 60 * 60,
            retry: 2,
            onError: (error: unknown) => {
                setClearviewErrorMsg(error.message);
            },
        }
    );
};

export const useClearviewMmsiExample = (mmsi: number | null, setClearviewErrorMsg: (value: string | null) => void) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/insights/mmsi`;

    return useQuery<FeatureCollection>(
        ['getMmsi', mmsi],
        async () => {
            const res = await fetch(`${url}?mmsi=${mmsi}`, {
                method: 'GET',
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                if (res.status === 401) {
                    throw new Error('Your user does not have access to the VAKE tool for locating dark vessels.');
                }
                throw new Error(`Failed to fetch Clearview MMSI data. Error fetching data: ${res.statusText}`);
            }
            return await res.json();
        },
        {
            staleTime: Infinity,
            enabled: !!mmsi,
            cacheTime: 1000 * 60 * 60,
            retry: 2,
            onError: (error: unknown) => {
                setClearviewErrorMsg(error.message);
            },
        }
    );
};

export type ManuallyValidateInput = {
    manually_validated: boolean | null;
    matching_label: string | null;
    // object_class: string | null;
};
export type ManuallyValidatePostRequest = ManuallyValidateInput & {
    uuid: string;
};
export const useUpdateInsightLabels = (date: Date, reportUUID: string | undefined) => {
    const url = `${vake_api_server_url}/insights/manually-validate`;
    const queryClient = useQueryClient();
    return useMutation<unknown, Error, ManuallyValidatePostRequest>({
        mutationFn: async (data) => {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            await queryClient.invalidateQueries([queryKeyUseInsightsPerDateQuery, reportUUID, date, true]); // TODO remove when OW 1.0 is replaced.
            await queryClient.invalidateQueries([queryKeyUseInsightsPerDateQuery, reportUUID, date, false]); // TODO remove when OW 1.0 is replaced.
            await queryClient.invalidateQueries([
                queryKeyUseInsightsPerDateQueryMinimal,
                reportUUID,
                date,
                data.matching_label,
            ]);
        },
    });
};
